import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { GoogleTasksSync } from '../components/GoogleTasksSync';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { startOfDay, endOfDay, subDays, isSameDay } from 'date-fns';

type LocationState = {
  tasks: string[];
  goal: string;
  goalImportance: string;
};

export function JournalComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState('');
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [journalStreak, setJournalStreak] = useState(0);
  const { tasks = [], goal = '', goalImportance = '' } = location.state as LocationState || {};

  useEffect(() => {
    async function fetchUserData() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('name, core_value_1, core_value_2, core_value_3')
          .eq('id', user.id)
          .single();

        if (profile?.name) {
          setUserName(profile.name.split(' ')[0]); // Get first name
        }
        
        if (profile) {
          setCoreValues([
            profile.core_value_1,
            profile.core_value_2,
            profile.core_value_3
          ].filter(Boolean));
        }

        // Calculate journal streak properly
        const { data: journalEntries } = await supabase
          .from('journal_entries')
          .select('created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (journalEntries?.length) {
          // Calculate streak by checking for consecutive days
          let streak = 0;
          const now = new Date();
          const today = startOfDay(now);
          
          // Check if there's an entry for today
          const hasEntryToday = journalEntries.some(entry => 
            isSameDay(new Date(entry.created_at), today)
          );
          
          // Start checking from yesterday or today depending on if there's an entry today
          let currentDate = hasEntryToday ? subDays(today, 1) : today;
          
          while (true) {
            const hasEntry = journalEntries.some(entry => 
              isSameDay(new Date(entry.created_at), currentDate)
            );
            
            if (!hasEntry) break;
            streak++;
            currentDate = subDays(currentDate, 1);
          }
          
          // If there's an entry today and we started checking from yesterday, add 1 to the streak
          if (hasEntryToday) {
            streak++;
          }
          
          setJournalStreak(streak);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    fetchUserData();
  }, []);

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle className="w-8 h-8 text-[#318231]" />
            <h1 className="text-3xl font-bold text-[#004060]">
              Congratulations{userName ? `, ${userName}` : ''}!
            </h1>
          </div>

          {journalStreak > 0 && (
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <p className="text-lg text-green-700 font-medium">
                Day {journalStreak} of journaling. Keep it up!
              </p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <p className="text-xl text-gray-700 mb-4">
                You created actions for today:
              </p>
              <ul className="space-y-2 mb-6">
                {tasks.map((task, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#318231]">•</span>
                    {task}
                  </li>
                ))}
              </ul>
            </div>

            {coreValues.length > 0 && goal && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Each one you complete reflects{' '}
                  <span className="text-[#236caf] font-medium">
                    {coreValues.slice(0, -1).join(', ')}
                    {coreValues.length > 1 ? ` and ${coreValues[coreValues.length - 1]}` : coreValues[0]}
                  </span>{' '}
                  and moves you closer to <strong>{goal}</strong>.
                </p>
                {goalImportance && (
                  <p className="text-gray-700">
                    Remember why this matters to you: <em>{goalImportance}</em>
                  </p>
                )}
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-lg">
              <GoogleTasksSync />
            </div>

            <p className="text-gray-700">
              Don't forget to check these off when you are done and document your lessons learned at the end of the day. That will give you more to celebrate tomorrow.
            </p>

            <p className="text-xl font-medium text-[#004060]">You got this.</p>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}